import { ReactNode, useMemo } from 'react';

import {
  AngleRight,
  ProviderAvatarBlock,
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraText,
  ChakraToolTip,
  DateFormat,
  EquipUIFireflyV2Theme,
  Flag,
  UserAvatarCard,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { capitalizeFirstLetter } from '~/lib/common';
import {
  UNASSIGNED_PATIENT,
  UNASSIGNED_USER,
  UNASSIGNED_PROVIDER,
} from '~/lib/constants/user.constants';
import {
  createCombinedNamesForFamilyTaskAssignees,
  getPreferredFullName,
} from '~/lib/util/patient.util';
import {
  getTaskListItemBgColor,
  getTaskListItemBorderColor,
  getTaskListItemDaysFromNow,
  taskPriorityDefinitions,
  taskStatusDefinitions,
} from '~/lib/util/task.util';
import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';

import CategoryDisplay from './CategoryDisplay';
import DueStatus from './DueStatus';
import StatusLabel from './StatusLabel';
import TitleAndCaption from './TitleAndCaption';
import UserContainer from './UserContainer';

const { neutral } = EquipUIFireflyV2Theme.colors;

const GridItem = ({
  children,
  colSpan,
}: {
  children: ReactNode;
  colSpan?: number;
}) => (
  <ChakraGridItem
    alignItems="center"
    colSpan={colSpan}
    display="flex"
    justifyContent="center"
  >
    {children}
  </ChakraGridItem>
);

export interface TaskListItemProps {
  isFamilyTask?: boolean;
  isPatientProfile?: boolean;
  onTaskSelect: (id: string) => void;
  task: Task;
}

const TaskListItem = ({
  isFamilyTask = false,
  isPatientProfile = false,
  onTaskSelect,
  task,
}: TaskListItemProps) => {
  const {
    assignee: _assignee,
    category,
    description,
    dueDate,
    name,
    patient: _patient,
    subcategory,
    taskExternalId,
    taskPriorityId,
    taskStatusId,
    familyAssignees,
  } = task;
  const {
    color,
    textColor,
    title: statusLabel,
  } = taskStatusDefinitions[Number.parseInt(taskStatusId, 10) - 1];
  const assignee: GetProviderByIdResponse = _assignee ?? UNASSIGNED_PROVIDER;
  const {
    avatarSrc: patientAvatarSrc,
    chosenName: patientChosenName,
    dateOfBirth: patientDob,
    firstName: patientFirstName,
    gender: patientGender,
    lastName: patientLastName,
    name: patientPreferredFullName,
    userId: patientUserId,
  }: Patient = _patient ?? UNASSIGNED_PATIENT;
  const familyAssignee: FamilyTaskAssignee =
    familyAssignees?.[0] ?? UNASSIGNED_USER;
  const familyAssigneeLength = familyAssignees?.length ?? 0;

  const daysFromNow = getTaskListItemDaysFromNow(dueDate);
  const assigneeNames = useMemo<string>(() => {
    if (familyAssignees && familyAssignees.length > 1) {
      return createCombinedNamesForFamilyTaskAssignees(familyAssignees);
    }
    return null;
  }, [familyAssignees]);

  const legalName = {
    firstName: assignee.firstName,
    lastName: assignee.lastName,
  };
  const displayName = {
    firstName: assignee.displayFirstName,
    lastName: assignee.displayLastName,
  };
  const isDisplayName = assignee.displayFirstName && assignee.displayLastName;
  const oooEndDate = getLatestOutOfOfficeEndDate(assignee.outOfOffice);

  const renderTaskAssignee = () => {
    if (isFamilyTask) {
      return (
        <ChakraFlex alignItems="center" justifyContent="space-around">
          <UserAvatarCard
            avatarSrc={familyAssignee.avatarSrc}
            id={`task-list-assignee__${taskExternalId}`}
            primaryText={[
              familyAssignee.name ||
                getPreferredFullName(
                  familyAssignee.chosenName,
                  familyAssignee.firstName,
                  familyAssignee.lastName,
                ),
            ]}
            secondaryText={[
              capitalizeFirstLetter(familyAssignee.userType ?? ''),
            ]}
            secondaryTextBold
            secondaryTextStyle={{ fontSize: '10px', lineHeight: '14px' }}
            showGenericIcon={familyAssignee.userId === UNASSIGNED_USER.userId}
          />
          {familyAssigneeLength > 1 && (
            <ChakraToolTip
              bg="white"
              border={`1px solid ${neutral.line}`}
              borderRadius="8px"
              color="neutral.primary"
              gutter={3}
              label={assigneeNames}
            >
              <ChakraText
                bottom="7px"
                marginX="8px"
                position="relative"
              >{`+${familyAssigneeLength - 1}`}</ChakraText>
            </ChakraToolTip>
          )}
        </ChakraFlex>
      );
    } else {
      return (
        <ProviderAvatarBlock
          avatarUrl={assignee?.avatarDetails?.thumbnailUrl}
          displayName={isDisplayName && displayName}
          id="task-list-assignee"
          legalName={legalName}
          oooDate={oooEndDate}
          providerTypeName={assignee.providerTypeAtAssignment}
          shouldShowOOOIcon={!!oooEndDate}
          shouldShowProviderType
          variant="standard"
        />
      );
    }
  };

  return (
    <ChakraSimpleGrid
      _hover={{ cursor: 'pointer' }}
      background={getTaskListItemBgColor(daysFromNow ?? 1, statusLabel)}
      borderColor={getTaskListItemBorderColor(daysFromNow ?? 1, statusLabel)}
      borderRadius="16px"
      borderStyle="solid"
      borderWidth="2px"
      gap={2}
      onClick={() => onTaskSelect(taskExternalId)}
      padding="23.5px 16px"
      templateColumns="repeat(17, 1fr)"
    >
      <ChakraGridItem colSpan={isPatientProfile ? 4 : 3}>
        <TitleAndCaption
          categoryName={category?.categoryName}
          description={description}
          name={name}
          subcategoryName={subcategory?.categoryName}
        />
      </ChakraGridItem>
      <ChakraGridItem colSpan={isPatientProfile ? 4 : 3}>
        <UserContainer>{renderTaskAssignee()}</UserContainer>
      </ChakraGridItem>
      {!isPatientProfile && (
        <ChakraGridItem colSpan={3}>
          <UserContainer>
            <UserAvatarCard
              avatarSrc={patientAvatarSrc}
              id={`task-list-patient__${taskExternalId}`}
              primaryText={[
                patientPreferredFullName ||
                  getPreferredFullName(
                    patientChosenName,
                    patientFirstName,
                    patientLastName,
                  ),
              ]}
              secondaryText={[
                patientGender ?? '',
                patientDob
                  ? DateTime.fromISO(patientDob).toFormat(DateFormat.longDate)
                  : '',
              ].filter((value: string) => value)}
              secondaryTextBold
              secondaryTextStyle={{ fontSize: '10px', lineHeight: '14px' }}
              showGenericIcon={patientUserId === UNASSIGNED_PATIENT.userId}
            />
          </UserContainer>
        </ChakraGridItem>
      )}
      <ChakraGridItem colSpan={2}>
        <CategoryDisplay
          categoryColor={category?.categoryColor}
          categoryName={category?.categoryName}
        />
      </ChakraGridItem>
      <ChakraGridItem colSpan={2}>
        <CategoryDisplay categoryName={subcategory?.categoryName} />
      </ChakraGridItem>
      <GridItem>
        <Flag
          fill={
            taskPriorityDefinitions[Number.parseInt(taskPriorityId, 10) - 1]
              .color
          }
          height="12px"
          width="12px"
        />
      </GridItem>
      <GridItem>
        <DueStatus
          daysFromNow={daysFromNow}
          dueDate={dueDate}
          statusLabel={statusLabel}
        />
      </GridItem>
      <GridItem colSpan={isPatientProfile ? 3 : 2}>
        <ChakraFlex
          alignItems="center"
          justifyContent="space-around"
          width="100%"
        >
          {/** Max width of StatusLabel is 69px (for status "In Progress"). To align StatusLabel in same line.  */}
          <ChakraBox width="69px">
            <StatusLabel
              color={color}
              status={statusLabel}
              textColor={textColor}
            />
          </ChakraBox>
          <AngleRight fill={neutral.secondary} height="12px" width="12px" />
        </ChakraFlex>
      </GridItem>
    </ChakraSimpleGrid>
  );
};

export default TaskListItem;
